import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { EHRProgrammingRefresh } from "@pages/ClinicalReportDetails/ReportPage/EHRProgrammingRefresh";
import { useBackgroundSync } from "@providers/BackgroundSyncProvider";
import { useReport } from "@providers/ReportProvider";

export const RefreshingData: React.FC = () => {
  const navigate = useNavigate();
  // Select programming passes its own location state through this step
  // (e.g. returnToReportPage); hand it back so its Back button still works.
  const { state } = useLocation();
  const { report, reportQuery } = useReport();
  const { clearBackgroundSync } = useBackgroundSync();
  const learnerExternalId = report?.learner?.externalId || null;

  // CR learning tree review: Lock sends the import it started here to wait on
  // (watchImportSince), and Refresh Now asks to come back to the review.
  const reviewState = (state ?? {}) as {
    watchImportSince?: string | null;
    returnToLearningTree?: boolean;
    learningTreeStructureOnly?: boolean;
  };
  const { watchImportSince, returnToLearningTree, learningTreeStructureOnly } = reviewState;
  // The rest of the state goes on unchanged (undefined stays undefined).
  const passThrough = useMemo(() => {
    if (!state) return state as undefined;
    const rest = { ...(state as Record<string, unknown>) };
    delete rest.watchImportSince;
    delete rest.returnToLearningTree;
    delete rest.learningTreeStructureOnly;
    return rest;
  }, [state]);
  const onImportSuccess = useCallback(() => {
    clearBackgroundSync();
    reportQuery.refetch();
    // Replace, so browser Back from select programming doesn't land here again
    // and start another import.
    const next = returnToLearningTree ? "../review-learning-tree" : "../select-programming";
    void navigate(next, { state: passThrough, replace: true });
  }, [reportQuery, navigate, clearBackgroundSync, passThrough, returnToLearningTree]);

  return (
    <EHRProgrammingRefresh
      learnerExternalId={learnerExternalId}
      onImportSuccess={onImportSuccess}
      watchImportSince={watchImportSince}
      learningTreeStructureOnly={!!learningTreeStructureOnly}
    />
  );
};
